import type { StaffingWorker } from './model';
import { planOrganizationValue, type StaffingPlanWorkerChange } from './planModel';

export const staffingPlanWorkerFields = [
  { key: 'organization', label: '소속 조직', inputLabel: '이동 조직' },
  { key: 'jobProfileId', label: '직무', inputLabel: '직무' },
  { key: 'jobLevelId', label: '직무 레벨', inputLabel: '직무 레벨' },
  { key: 'jobTypeId', label: '고용 유형', inputLabel: '고용 유형' },
  { key: 'timeType', label: '근무 형태', inputLabel: '근무 형태' },
  { key: 'locationId', label: '근무지', inputLabel: '근무지' },
  { key: 'workSpaceId', label: '업무 공간', inputLabel: '업무 공간' },
  { key: 'managerId', label: '보고 관리자', inputLabel: '보고 관리자' },
  { key: 'businessTitle', label: '직함', inputLabel: '변경할 직함' },
] as const;
export type StaffingPlanWorkerField = typeof staffingPlanWorkerFields[number]['key'];
export type StaffingPlanWorkerPatch = Omit<Pick<StaffingPlanWorkerChange, StaffingPlanWorkerField | 'businessTitleMode' | 'clearWorkSpace' | 'clearManager'>, 'timeType'> & { timeType?: '' | 'FULL_TIME' | 'PART_TIME' };

export function workerChangePatch(row: StaffingPlanWorkerChange): StaffingPlanWorkerPatch {
  const patch: StaffingPlanWorkerPatch = {};
  for (const { key } of staffingPlanWorkerFields) {
    if (key === 'organization') { if (row.organization !== undefined) patch.organization = row.organization; }
    else if (key === 'managerId') { if (row.managerId !== undefined || row.clearManager) { patch.managerId = row.managerId ?? ''; patch.clearManager = row.clearManager; } }
    else if (key === 'businessTitle') {
      if (row.businessTitleMode === 'CLEAR' || (row.businessTitle !== undefined && row.businessTitleMode !== 'KEEP')) {
        patch.businessTitle = row.businessTitleMode === 'CLEAR' ? '' : row.businessTitle;
        patch.businessTitleMode = row.businessTitleMode ?? (row.businessTitle === '' ? 'CLEAR' : 'CHANGE');
      }
    } else if (key === 'timeType') { if (row.timeType !== undefined) patch.timeType = row.timeType; }
    else if (key === 'workSpaceId') {
      if (row.workSpaceId !== undefined || row.clearWorkSpace) { patch.workSpaceId = row.workSpaceId ?? ''; patch.clearWorkSpace = row.clearWorkSpace; }
    } else if (row[key] !== undefined) patch[key] = row[key];
  }
  return patch;
}

export const selectedWorkerFields = (patch: StaffingPlanWorkerPatch) => staffingPlanWorkerFields.filter(({ key }) => Object.hasOwn(patch, key) || (key === 'workSpaceId' && patch.clearWorkSpace) || (key === 'managerId' && patch.clearManager));

/** 선택한 키만 덮어씁니다. 선택하지 않은 개인별 입력과 대상 식별자는 그대로 보존합니다. */
export function mergeWorkerChangePatch(row: StaffingPlanWorkerChange, patch: StaffingPlanWorkerPatch, worker?: StaffingWorker): StaffingPlanWorkerChange {
  const next = { ...row };
  for (const { key } of selectedWorkerFields(patch)) {
    if (key === 'organization') next.organization = patch.organization ? { ...patch.organization } : undefined;
    else if (key === 'managerId') { next.managerId = patch.clearManager ? undefined : patch.managerId; next.clearManager = patch.clearManager || undefined; }
    else if (key === 'businessTitle') {
      next.businessTitleMode = patch.businessTitleMode ?? 'CHANGE';
      next.businessTitle = next.businessTitleMode === 'CLEAR' ? '' : patch.businessTitle;
    } else if (key === 'timeType') next.timeType = patch.timeType;
    else if (key === 'workSpaceId') { next.workSpaceId = patch.clearWorkSpace ? undefined : patch.workSpaceId; next.clearWorkSpace = patch.clearWorkSpace || undefined; }
    else next[key] = patch[key];
  }
  // 근무지가 바뀌면 이전 근무지의 공간을 남기지 않습니다. 공통 입력에서도 대상별 현재 값을 기준으로 판단합니다.
  if (patch.locationId && patch.locationId !== (row.locationId || worker?.locationId) && !selectedWorkerFields(patch).some(field => field.key === 'workSpaceId')) {
    delete next.workSpaceId;
    delete next.clearWorkSpace;
  }
  if (worker?.workSpaceId && next.locationId && next.locationId !== worker.locationId && !next.workSpaceId) next.clearWorkSpace = true;
  return next;
}

/** 단건 편집에서 제외한 항목은 발령안에서도 제거해 현재 값을 유지합니다. */
export function replaceWorkerChangePatch(row: StaffingPlanWorkerChange, patch: StaffingPlanWorkerPatch, worker?: StaffingWorker): StaffingPlanWorkerChange {
  const next = { ...row };
  for (const { key } of staffingPlanWorkerFields) delete next[key];
  delete next.businessTitleMode;
  delete next.clearWorkSpace;
  delete next.clearManager;
  return mergeWorkerChangePatch(next, patch, worker);
}

function fieldValue(row: StaffingPlanWorkerPatch, key: StaffingPlanWorkerField) {
  if (key === 'organization') return row.organization ? planOrganizationValue(row.organization) : undefined;
  if (key === 'managerId') return row.clearManager ? '' : row.managerId;
  if (key === 'workSpaceId') return row.clearWorkSpace ? '' : row.workSpaceId;
  if (key === 'businessTitle') return row.businessTitleMode === 'CLEAR' ? '' : row.businessTitleMode === 'KEEP' ? undefined : row.businessTitle?.trim();
  return row[key];
}

export function workerPatchOverwriteCount(rows: StaffingPlanWorkerChange[], patch: StaffingPlanWorkerPatch): number {
  return rows.filter(row => (Boolean(patch.locationId && row.workSpaceId && patch.locationId !== row.locationId && !selectedWorkerFields(patch).some(field => field.key === 'workSpaceId'))) || selectedWorkerFields(patch).some(({ key }) => {
    const before = fieldValue(row, key);
    return before !== undefined && before !== fieldValue(patch, key);
  })).length;
}

export function validateWorkerChangePatch(patch: StaffingPlanWorkerPatch, workers: StaffingWorker[]): string | undefined {
  if (!workers.length) return '대상 구성원을 선택해 주세요.';
  if (!selectedWorkerFields(patch).length) return '변경 항목을 하나 이상 추가해 주세요.';
  for (const { key, label } of selectedWorkerFields(patch)) {
    if (key === 'organization') {
      if (!planOrganizationValue(patch.organization)) return '이동 조직을 선택해 주세요.';
    } else if (key === 'businessTitle') {
      if (patch.businessTitleMode !== 'CLEAR' && !patch.businessTitle?.trim()) return '변경할 직함을 입력하거나 직함 해제를 선택해 주세요.';
    } else if (key === 'managerId' && patch.clearManager) {
      if (patch.managerId) return '보고 관리자 지정과 해제를 함께 선택할 수 없습니다.';
    } else if (key === 'workSpaceId' && patch.clearWorkSpace) {
      if (patch.workSpaceId) return '업무 공간 변경과 해제를 함께 지정할 수 없습니다.';
    } else if (!patch[key]?.trim()) return `${label}을 선택해 주세요.`;
  }
  if (patch.timeType && !['FULL_TIME', 'PART_TIME'].includes(patch.timeType)) return '근무 형태를 다시 선택해 주세요.';
  if (Object.hasOwn(patch, 'jobTypeId') && new Set(workers.map(worker => worker.workerTypeId === 'CONTINGENT_WORKER' ? 'CONTINGENT_WORKER' : 'EMPLOYEE')).size > 1) {
    return '직원과 외부 인력의 유형은 함께 변경할 수 없습니다. 유형 변경을 제외하거나 같은 인력 유형끼리 선택해 주세요.';
  }
  return undefined;
}

export function currentWorkerField(worker: StaffingWorker, key: StaffingPlanWorkerField): string | undefined {
  const value = key === 'timeType' ? worker.timeType === 'FULL_TIME' ? '전일제' : worker.timeType === 'PART_TIME' ? '시간제' : undefined
    : key === 'managerId' ? worker.manager : key === 'workSpaceId' ? worker.workSpace : key === 'organization' ? worker.organization : key === 'jobProfileId' ? worker.jobProfile : key === 'jobLevelId' ? worker.jobLevel
    : key === 'jobTypeId' ? worker.jobType : key === 'locationId' ? worker.location : worker.businessTitle;
  return !value || value === '—' || value === '미지정' || value === '없음' ? undefined : value;
}
